import { ApiAddress, ApiCreateAddressBody } from '../api-types/address.api';
import { Address, CreateAddressBody } from '../app-types/address.app';
import { removeWhitespaceAndNewlines } from './helpers';

/**
 * Transforms an ApiAddress object to an app-specific Address object.
 * @param input ApiAddress
 * @description Transforms an ApiAddress object to an app-specific Address object.
 * This function maps the properties of the ApiAddress to the Address interface.
 * It is used to convert data received from the API into a format that the application can work with.
 * @returns Address
 * @example
 * const apiAddress: ApiAddress = {
 *   streetAndHouseNumber: '123 Main St',
 *   postalCodeAndCity: '12345 City',
 *   soundexStreet: '12345',
 */
export const transformApiAddressToAppAddress = (input: ApiAddress) => {
	const output: Address = {
		id: input.id,
		streetAndHouseNumber: removeWhitespaceAndNewlines(input.streetAndHouseNumber),
		postalCodeAndCity: removeWhitespaceAndNewlines(input.postalCodeAndCity),
		soundexStreet: removeWhitespaceAndNewlines(input.soundexStreet),
		anonym: removeWhitespaceAndNewlines(input.anonym),
		identifier: removeWhitespaceAndNewlines(input.identifier),
	};
	return output;
};

/**
 * Transforms an array of ApiAddress objects to an array of app-specific Address objects.
 * @param input Array of ApiAddress objects
 * @description Transforms an array of ApiAddress objects to an array of app-specific Address objects.
 * This function is useful when you need to convert multiple addresses received from the API into a format
 * that the application can work with. It uses the `transformApiAddressToAppAddress`
 * function to transform each individual address.
 * @returns Array of Address objects
 */
export const transformApiAddressesToAppAddresses = (input: ApiAddress[]) => {
	const output: Address[] = input.map(transformApiAddressToAppAddress);
	return output;
};

/**
 * Transforms an app-specific Address object to an ApiAddress object.
 * @param input Address
 * @description Transforms an app-specific Address object to an ApiAddress object.
 * This function maps the properties of the Address to the ApiAddress interface.
 * It is used to convert data from the application into a format that can be sent to the API.
 * @returns ApiAddress
 */
export const transformAppAddressToApiAddress = (input: Address) => {
	const output: ApiAddress = {
		id: input.id,
		streetAndHouseNumber: input.streetAndHouseNumber,
		postalCodeAndCity: input.postalCodeAndCity,
		soundexStreet: input.soundexStreet,
		anonym: input.anonym,
		identifier: input.identifier,
	};
	return output;
};

/**
 * Transforms an app-specific CreateAddressBody object to an ApiCreateAddressBody object.
 * @param input CreateAddressBody
 * @description Transforms an app-specific CreateAddressBody object to an ApiCreateAddressBody object.
 * This function maps the properties of the CreateAddressBody to the ApiCreateAddressBody interface.
 * It is used to convert data from the application into a format that can be sent to the API.
 * @returns ApiCreateAddressBody
 */
export const transformAppCreateAddressToApiCreateAddressBody = (input: CreateAddressBody) => {
	const output: ApiCreateAddressBody = {
		streetAndHouseNumber: input.streetAndHouseNumber,
		postalCodeAndCity: input.postalCodeAndCity,
		soundexStreet: input.soundexStreet,
		anonym: input.anonym,
		identifier: input.identifier,
	};
	return output;
};
